import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, FileText, Car, Users, LogOut,
  CalendarDays, Receipt, UserCheck,
  FileUp, Settings, Contact, ChevronRight, BarChart2, ClipboardList, Menu, X
} from "lucide-react";

const ADMIN_NAV_GROUPS = [
  {
    label: "TABLEAU DE BORD",
    items: [
      { path: "/admin/dashboard", icon: LayoutDashboard, label: "Tableau de bord" },
    ]
  },
  {
    label: "COMMERCIAL",
    items: [
      { path: "/admin/devis", icon: FileText, label: "Devis & Réservations" },
      { path: "/admin/import-pdf", icon: FileUp, label: "Import PDF (IA)" },
      { path: "/admin/factures", icon: Receipt, label: "Factures" },
    ]
  },
  {
    label: "CLIENTÈLE",
    items: [
      { path: "/admin/clients", icon: UserCheck, label: "Clients" },
      { path: "/admin/contacts", icon: Contact, label: "Contacts" },
    ]
  },
  {
    label: "OPÉRATIONS",
    items: [
      { path: "/admin/reservations", icon: CalendarDays, label: "Calendrier & Prévisionnel" },
      { path: "/admin/stats", icon: BarChart2, label: "Statistiques" },
    ]
  },
  {
    label: "ADMINISTRATION",
    items: [
      { path: "/admin/vehicules", icon: Car, label: "Véhicules" },
      { path: "/admin/utilisateurs", icon: Users, label: "Utilisateurs" },
      { path: "/admin/parametres", icon: Settings, label: "Paramètres" },
    ]
  },
];

const USER_NAV_GROUPS = [
  {
    label: "MES PRESTATIONS",
    items: [
      { path: "/admin/devis", icon: ClipboardList, label: "Mes Réservations" },
    ]
  },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/admin"); };

  const isAdmin = user?.role === "admin";
  const NAV_GROUPS = isAdmin ? ADMIN_NAV_GROUPS : USER_NAV_GROUPS;

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="px-5 py-5 border-b border-[#243d5e] flex items-center justify-between">
        <div>
          <p className="text-white font-bold text-base leading-tight tracking-tight">RM Location</p>
          <p className="text-[#D4A843] text-[9px] font-semibold tracking-[0.2em] uppercase mt-0.5">Administration</p>
        </div>
        <button onClick={() => setMobileOpen(false)} className="text-slate-400 hover:text-white md:hidden">
          <X size={18} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 overflow-y-auto">
        {NAV_GROUPS.map((group) => (
          <div key={group.label} className="mb-1">
            <p className="px-4 py-2 text-[9px] font-bold tracking-[0.15em] uppercase text-slate-500 select-none">
              {group.label}
            </p>
            {group.items.map(({ path, icon: Icon, label }) => {
              const active = location.pathname === path;
              return (
                <Link
                  key={path}
                  to={path}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`nav-${path.split("/").pop()}`}
                  className={`flex items-center gap-2.5 px-4 py-2 mx-2 rounded text-sm transition-all mb-0.5 ${
                    active
                      ? "bg-blue-50 text-blue-700 font-semibold"
                      : "text-slate-400 hover:bg-[#243d5e] hover:text-white"
                  }`}
                >
                  <Icon size={14} className={active ? "text-blue-600" : ""} />
                  <span className="leading-tight">{label}</span>
                  {active && <ChevronRight size={12} className="ml-auto text-blue-400" />}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User + Logout */}
      <div className="px-4 py-4 border-t border-[#243d5e]">
        {user && (
          <div className="mb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">{user.nom?.[0]?.toUpperCase() || "A"}</span>
              </div>
              <div className="min-w-0">
                <p className="text-white text-xs font-semibold truncate">{user.nom}</p>
                <p className="text-slate-500 text-[10px] truncate">{user.role === "admin" ? "Administrateur" : "Manager"}</p>
              </div>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="flex items-center gap-2 text-slate-400 hover:text-red-400 text-xs transition-colors w-full mt-1"
        >
          <LogOut size={13} /> Déconnexion
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex w-56 bg-[#1B2E4B] flex-col flex-shrink-0 shadow-xl">
        <SidebarContent />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar mobile (drawer) */}
      <aside className={`fixed top-0 left-0 h-full w-56 bg-[#1B2E4B] flex flex-col shadow-xl z-50 transition-transform duration-200 md:hidden ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <SidebarContent />
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto min-h-screen bg-slate-50">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center gap-3 px-4 py-3 bg-[#1B2E4B] shadow">
          <button onClick={() => setMobileOpen(true)} className="text-white">
            <Menu size={22} />
          </button>
          <p className="text-white font-bold text-sm">RM Location</p>
        </div>

        {children}
      </main>
    </div>
  );
}
